import { useCallback, useState } from "react";

import {
  AchPaymentMethod,
  CreditCardPaymentMethod,
  useRemovePolicyStripePaymentMethodMutation,
} from "@covertree/agent-portal-graphql";
import { Toast } from "@covertree/utils";

export type RemovablePaymentMethod = CreditCardPaymentMethod | AchPaymentMethod;

export function useRemoveStripePaymentMethod(policyLocator: string) {
  const [methodToRemove, setMethodToRemove] =
    useState<RemovablePaymentMethod>();

  const [removePolicyStripeMethod, { loading }] =
    useRemovePolicyStripePaymentMethodMutation();

  const onRemoveMethodModalClose = useCallback(() => {
    if (loading) {
      return;
    }
    setMethodToRemove(undefined);
  }, [loading]);

  const removeMethod = useCallback(async () => {
    if (!methodToRemove || loading) {
      return;
    }

    try {
      await removePolicyStripeMethod({
        variables: { policyLocator, paymentMethodId: methodToRemove.id },
        update(cache) {
          const typeName =
            "brand" in methodToRemove
              ? "CreditCardPaymentMethod"
              : "AchPaymentMethod";
          const normalizedId = cache.identify({
            id: methodToRemove.id,
            __typename: typeName,
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        },
      });
      setMethodToRemove(undefined);
    } catch (e) {
      Toast.showError("Could not remove payment method. Please try again.");
    }
  }, [methodToRemove, loading, removePolicyStripeMethod, policyLocator]);

  return {
    methodToRemove,
    setMethodToRemove,
    onRemoveMethodModalClose,
    removeMethod,
    loading,
  };
}
